<template>
  <SbDropdownItem
    :tag="as"
    :to="to"
    class="sb-minibrowser__list-item"
    :label="label"
    :description="subtitle"
    :icon="iconProps.name"
    :icon-color="iconProps.color"
    :class="{
      'sb-minibrowser__list-item--active': isActive,
      'sb-minibrowser__list-item--disabled': isDisabled,
    }"
    hide-description
    @click="handleClick"
    @keydown="handleKeydown"
  >
    <template #icon>
      <SbIcon v-if="!$slots.icon" v-bind="iconProps" />
      <slot name="icon" />
    </template>
    <template v-if="$slots.label" #label>
      <slot name="label" />
    </template>
    <template v-if="$slots.description" #description>
      <slot name="description" />
    </template>
    <slot />
  </SbDropdownItem>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { SbDropdownItem } from '../../Dropdown'
import SbIcon, { type IconColor, type IconName } from '../../Icon'
import { useMinibrowser } from '../utils'
import type { MinibrowserOption, MinibrowserSelectItemPayload } from '../types'

type SbMinibrowserListItemProps = {
  /**
   * The tag to be used in the item. Default is 'a'
   */
  as?: string

  /**
   * If the item is active
   */
  isActive?: boolean

  /**
   * If the item is a parent
   */
  isParent?: boolean

  /**
   * If the item is disabled
   */
  isDisabled?: boolean

  /**
   * List of items to be displayed in the minibrowser. Used when the item is a parent
   */
  items?: MinibrowserOption[]

  /**
   * Id of the item
   */
  id?: string

  /**
   * Label of the item
   */
  label?: string

  /**
   * Subtitle of the item
   */
  subtitle?: string

  /**
   * The value of the item
   */
  modelValue?: string | number

  /**
   * The route to be used in the item. From Vue Router
   */
  to?: Record<string, unknown>
}

const props = withDefaults(defineProps<SbMinibrowserListItemProps>(), {
  as: 'a',
  items: undefined,
  id: undefined,
  label: undefined,
  subtitle: undefined,
  modelValue: undefined,
  to: undefined,
})

const emit = defineEmits<{
  (e: 'select', payload: MinibrowserSelectItemPayload): void
}>()

const { context } = useMinibrowser()

const iconProps = computed<{ name: IconName; color: IconColor }>(() => ({
  name: props.isParent ? 'folder' : 'status',
  color: 'primary',
}))

const handleClick = () => {
  const payload = {
    id: props.id,
    label: props.label,
    subtitle: props.subtitle,
    isParent: props.isParent,
    value: props.modelValue,
  }

  emit('select', payload)
  context.value.selectItem(payload)
}

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Enter' || event.key === ' ') {
    handleClick()
  }
}
</script>
